import SpotProfileLayout from "@layouts/SpotProfileLayout";
import {
  getSpotActivities,
  getSpotInfo,
  getSpotPlacesToVisit,
} from "@lib/api";
import { SECTIONS } from "@lib/constants";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useTranslation } from "next-i18next";

import type { Locale } from "../../../../i18n-config";
import { capitalize, pickByLocale } from "@lib/utils";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import { MapBoxSearch } from "@components/spot/spotMobile/Map/MapBox-search";
import TabSearch from "@components/spotprofile/tab-search";

export default function ExperiencesPage({
  cityName,
  backgroundImage,
  experiences,
  placesToVisit,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation();

  const locale = (useRouter().locale ?? "en") as Locale;

  const title = `${cityName} | ${capitalize(t("spotprofile.map.title"))}`;
  const description = pickByLocale(
    {
      en: `Explore ${cityName} with our interactive map`,
      es: `Explora ${cityName} con nuestro mapa interactivo`,
      fr: `Explorez ${cityName} avec notre carte interactive`,
      ru: `Исследуйте ${cityName} с нашей интерактивной картой`
    },
    locale
  );

  const section = Cookies.get('SPOTFILTER');

  var sectionFilter;
  if (section === 'plc')
    sectionFilter = SECTIONS[0];
  else if (section === 'exp')
    sectionFilter = SECTIONS[1];
  else sectionFilter = SECTIONS[0];

  return (
    <SpotProfileLayout
      cityName={cityName}
      backgroundImage={backgroundImage}
      meta={{
        title,
        description,
      }}
      search={true}
    >
      <TabSearch text={``} count={0}>
        <MapBoxSearch
          section={sectionFilter}
          placesToVisit={placesToVisit}
          experiences={experiences}
        />
      </TabSearch>
    </SpotProfileLayout>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const cityId = ctx.params?.id as string;
  const locale = ctx.locale as "en" | "es" | "fr" | "ru";

  const {
    data: [[spotInfo]],
  } = await getSpotInfo(cityId);

  const {
    data: [experiences],
  } = await getSpotActivities(cityId);

  const {
    data: [placesToVisit],
  } = await getSpotPlacesToVisit(cityId);

  return {
    props: {
      cityName:
        spotInfo[
          locale === "es"
            ? "spanish_name"
            : locale === "fr"
            ? "french_name"
            : locale === "ru" && spotInfo.russian_name != null
            ? "russian_name"
            : "english_name"
        ],
      backgroundImage: spotInfo.spot_image,
      experiences: experiences
        .filter(
          ({ activity }) =>
            activity.filter((activity) => !!activity[`name_${locale}`]).length >
            0
        )
        .map((experience) => ({
          ...experience,
          activity: experience.activity.filter(
            (activity) => !!activity[`name_${locale}`]
          ),
        })),
      placesToVisit: placesToVisit
        .filter(
          ({ activity }) =>
            activity.filter((activity) => !!activity[`name_${locale}`]).length >
            0
        )
        .map((placeToVisit) => ({
          ...placeToVisit,
          activity: placeToVisit.activity.filter(
            (activity) => !!activity[`name_${locale}`]
          ),
        })),
    },
  };
}
